/**
 * ProjectSelector component for selecting a project from a dropdown
 * Used on orchestration page to associate sessions with projects
 */
import * as React from 'react'
import { Check, ChevronsUpDown, X, Archive, Loader2, FolderKanban } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useProjects } from '@/api/projects'
import type { ProjectSummary } from '@/lib/types'

export interface ProjectSelectorProps {
  /** List of available projects (if not using hook version) */
  projects?: ProjectSummary[]
  /** Currently selected project ID */
  selectedProjectId?: string | null
  /** Alias for selectedProjectId to match simpler API */
  value?: string
  /** Callback when project selection changes */
  onSelect?: (projectId: string | null) => void
  /** Alias for onSelect to match simpler API */
  onChange?: (projectId: string | undefined) => void
  /** Whether to show archived projects */
  showArchived?: boolean
  /** Whether selection can be cleared */
  clearable?: boolean
  /** Alias for clearable */
  allowClear?: boolean
  /** Whether the selector is disabled */
  disabled?: boolean
  /** Whether projects are loading */
  isLoading?: boolean
  /** Accessible label for the selector */
  label?: string
  /** Placeholder text */
  placeholder?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Dropdown selector for choosing a project.
 * Supports search, archived project filtering, and optional clearing.
 * Can either receive projects as props or fetch them internally.
 *
 * @example
 * ```tsx
 * // Simple usage with value/onChange (fetches projects internally)
 * <ProjectSelector
 *   value={projectId}
 *   onChange={setProjectId}
 *   allowClear
 * />
 *
 * // Full control with projects prop
 * <ProjectSelector
 *   projects={projects}
 *   selectedProjectId={projectId}
 *   onSelect={setProjectId}
 *   clearable
 * />
 * ```
 */
export function ProjectSelector({
  projects: projectsProp,
  selectedProjectId,
  value,
  onSelect,
  onChange,
  showArchived = false,
  clearable = true,
  allowClear,
  disabled = false,
  isLoading: isLoadingProp = false,
  label = 'Project',
  placeholder = 'Select a project...',
  className
}: ProjectSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')

  // Only use the projects hook when projects are not provided via props
  // This allows the component to work without QueryClientProvider when projects are passed directly
  const shouldFetchProjects = projectsProp === undefined
  const { data: fetchedProjectsData, isLoading: isFetching } = useProjects(
    { includeArchived: showArchived },
    { enabled: shouldFetchProjects }
  )

  // Determine projects source
  const projects = projectsProp ?? fetchedProjectsData?.projects ?? []
  const isLoading = isLoadingProp || (shouldFetchProjects && isFetching)

  // Normalize props (support both APIs)
  const selectedId = selectedProjectId ?? value ?? null
  const handleSelection = onSelect ?? ((id: string | null) => onChange?.(id ?? undefined))
  const canClear = clearable || allowClear

  // Filter projects based on archived flag
  const filteredProjects = React.useMemo(() => {
    if (!showArchived) {
      return projects.filter(p => !p.isArchived)
    }
    return projects
  }, [projects, showArchived])

  // Get selected project
  const selectedProject = React.useMemo(
    () => projects.find(p => p.id === selectedId),
    [projects, selectedId]
  )

  // Handle selection
  const handleSelect = (projectId: string) => {
    handleSelection(projectId === selectedId ? null : projectId)
    setOpen(false)
    setSearch('')
  }

  // Handle clear
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleSelection(null)
  }

  // Loading state
  if (isLoading) {
    return (
      <Button
        variant="outline"
        disabled
        className={cn('w-full justify-start', className)}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading projects...
      </Button>
    )
  }

  // Empty state
  if (filteredProjects.length === 0 && !selectedProject) {
    return (
      <Button
        variant="outline"
        disabled
        className={cn('w-full justify-start text-muted-foreground', className)}
      >
        <FolderKanban className="mr-2 h-4 w-4" />
        No projects available
      </Button>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          disabled={disabled}
          className={cn(
            'w-full justify-between',
            !selectedProject && 'text-muted-foreground',
            className
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <FolderKanban className="h-4 w-4 shrink-0" />
            {selectedProject ? (
              <span className="flex items-center gap-2 truncate">
                <span className="truncate">{selectedProject.name}</span>
                {selectedProject.isArchived && (
                  <Archive className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
            ) : (
              placeholder
            )}
          </span>

          <span className="flex items-center gap-1 shrink-0">
            {canClear && selectedProject && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e as unknown as React.MouseEvent) }}
                className="p-0.5 rounded hover:bg-muted cursor-pointer"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search projects..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No projects found.</CommandEmpty>
            <CommandGroup>
              {filteredProjects
                .filter(project =>
                  project.name.toLowerCase().includes(search.toLowerCase()) ||
                  project.description?.toLowerCase().includes(search.toLowerCase())
                )
                .map(project => (
                  <CommandItem
                    key={project.id}
                    value={project.id}
                    onSelect={() => handleSelect(project.id)}
                    className="flex items-center gap-2"
                  >
                    <Check
                      className={cn(
                        'h-4 w-4 shrink-0',
                        selectedId === project.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-medium">
                          {project.name}
                        </span>
                        {project.isArchived && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Archive className="h-3 w-3" />
                            archived
                          </span>
                        )}
                      </span>
                      {project.description && (
                        <span className="text-xs text-muted-foreground truncate">
                          {project.description}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {project.sessionCount} sessions
                    </span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
